import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Save, Eye, Sparkles, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Sample data used for live preview, so the user can see how the placeholders
// will be rendered with a real lead.
const PREVIEW_LEAD = {
  firmenname: 'Müller Dächer GmbH',
  ansprechpartner: 'Anna Müller',
  anrede: 'Sehr geehrte Frau Müller',
  branche: 'Dachdecker',
  standort: 'Wernigerode',
};

export default function EmailTemplateEditor({ user, company }) {
  const [template, setTemplate] = useState({ subject: '', body: '' });
  const [placeholders, setPlaceholders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef(null);

  useEffect(() => {
    api.get('/settings/email-template')
      .then(res => {
        setTemplate(res.data.template || { subject: '', body: '' });
        setPlaceholders(res.data.placeholders || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/email-template', template);
      toast.success('E-Mail-Vorlage gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = template.body.slice(0, start) + key + template.body.slice(end);
    setTemplate({ ...template, body: next });
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + key.length, start + key.length);
    }, 10);
  };

  const renderPreview = (text) => {
    const mapping = {
      ...PREVIEW_LEAD,
      absender_name: user?.name || 'Mein Name',
      absender_firma: company?.name || 'Meine Firma',
    };
    let out = text || '';
    Object.entries(mapping).forEach(([k, v]) => {
      out = out.replaceAll(`{{${k}}}`, v);
    });
    return out;
  };

  if (loading) {
    return <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Lädt...</CardContent></Card>;
  }

  return (
    <div className="space-y-6" data-testid="email-template-editor">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Eigene E-Mail-Vorlage</CardTitle>
          <CardDescription>
            Lege deinen persönlichen Outreach-Text einmal an. Beim Versenden an einen Lead
            werden Platzhalter automatisch durch die echten Firmendaten ersetzt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label htmlFor="tpl-subject">Betreff</Label>
            <Input
              id="tpl-subject"
              data-testid="email-template-subject"
              value={template.subject}
              onChange={e => setTemplate({ ...template, subject: e.target.value })}
              placeholder="z.B. Kooperationsanfrage von {{absender_firma}}"
              className="mt-1.5"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="tpl-body">Nachricht</Label>
              <span className="text-xs text-muted-foreground">{template.body.length} / 10000 Zeichen</span>
            </div>
            <textarea
              id="tpl-body"
              ref={bodyRef}
              data-testid="email-template-body"
              value={template.body}
              onChange={e => setTemplate({ ...template, body: e.target.value })}
              placeholder={'{{anrede}},\n\nich melde mich, weil...\n\nBeste Grüße\n{{absender_name}}'}
              rows={14}
              maxLength={10000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono"
            />
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Platzhalter (Klick fügt ein)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {placeholders.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  title={p.description}
                  data-testid={`placeholder-btn-${p.key.replace(/[{}]/g,'')}`}
                  className="px-2.5 py-1 text-xs font-mono rounded-md bg-muted hover:bg-emerald-100 hover:text-emerald-900 transition-colors border border-transparent hover:border-emerald-300"
                >
                  {p.key}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-email-template-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Speichert...' : 'Vorlage speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Eye className="w-4 h-4" /> Live-Vorschau</CardTitle>
          <CardDescription>So sieht deine Vorlage aus mit einem Beispiel-Lead (Müller Dächer GmbH).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Betreff:</span>
              <p className="font-medium" data-testid="preview-subject">{renderPreview(template.subject) || <em className="text-muted-foreground/60">(noch leer)</em>}</p>
            </div>
            <div className="border-t border-border/50 pt-3">
              <span className="text-xs text-muted-foreground">Nachricht:</span>
              <p className="text-sm whitespace-pre-wrap mt-1" data-testid="preview-body">{renderPreview(template.body) || <em className="text-muted-foreground/60">(noch leer)</em>}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
            <Badge variant="secondary" className="mr-1">Tipp</Badge>
            <span>Du kannst deine Vorlage später bei jedem Lead in der Outreach-Ansicht entweder direkt nutzen oder durch den KI-Vorschlag ersetzen.</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
